import { isEqual } from 'lodash'
import queryString from 'query-string'
import { useCallback, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { useDebounceValue, useQueryParams } from 'src/hooks'
import { LocationState } from 'src/redux/createRootReducer'

type KeyStr<T> = Extract<keyof T, string>

export type UseFilterOptions<T, M> = {
  /** function to translate query params to filters */
  normalizeQueryParams?: (
    queryParams: queryString.ParsedQuery<string>,
  ) => Partial<T>
  /** apply these filters if query params are empty */
  defaultFilters: T
  /** these filters are auto-applied on load but are NOT defaults */
  initialFilters?: T
  /** function to translate filters to api request */
  apiQueryParams: (filters: Partial<T>) => M
  /** function to translate filters to URL */
  stringifyFilters?: (filters: Partial<T>) => string
  equalsMap?: Partial<Record<KeyStr<T>, (a: any, b: any) => boolean>>
  diff?: (
    defaults: T,
    current: Partial<T>,
  ) => { changedKeys: (keyof T)[]; count: number }
}

type UseFiltersReturnType<T, M> = {
  /** current filters state */
  filters: Partial<T>
  /** serializable object for an API call */
  apiQueryParams: M
  /** to handle updates to individual filters */
  handleFilterChange: (newFilters: Partial<T>) => void
  /** to reset filters to default */
  resetFilters: () => void
  /** to reset a single filter to default */
  resetSingleFilter: (key: keyof T) => void
  /** boolean representing divergence from defaultFilters */
  isDirty: boolean
  selectedFiltersCount: number
  changedFilterKeys: (keyof T)[]
}

/**
 * Using this hook updates the URL as filters change
 * Engineers are expected to consume all of the return types and use the handleFilterChange function returned by this hook
 * This hook doesn't make the API call, but prepares a serializable object for an API call dependent
 * on whatever parameters and generics passed in.
 */

/**
 * useFilters<T, M>
 *
 * React hook for managing UI filter state and deriving API request params.
 * It keeps a `filters` state (based on your defaults), updates it via helpers,
 * and exposes a derived `apiQueryParams` value suitable for calling your API.
 * Using this hook updates the URL as filters change via history.replace, but
 *
 * @template T Filters shape consumed by your filter UI/components (what the user edits).
 *             This is usually UI-friendly and may include arrays, strings, etc.
 *             Example:
 *             interface ExpenseCategoryFilters {
 *               search?: string;
 *               status?: string[];
 *               required?: string[];
 *             }
 *
 * @template M Request params shape sent to your API (what your backend expects).
 *             This is often renamed/normalized relative to T.
 *             Example:
 *             type ExpenseCategoryFiltersRequestParams = {
 *               search?: string;
 *               active?: boolean;
 *               required?: boolean;
 *               sortField?: ExpenseCategorySortField;
 *               sortDirection?: 'ASC' | 'DESC';
 *             }
 *
 * @param options.normalizeQueryParams Optional function to normalize raw query params
 *                                     from the URL to filters.
 * @param options.defaultFilters Default values for the filters. The keys
 *                               here also define the allowed set of filter keys.
 * @param options.initialFilters Initial values for the filters that are shown on top of the
 *                               defaults. Values defined here are counted towards
 *                               selectedFiltersCount and can be reset.
 * @param options.apiQueryParams Mapping from the current filters (Partial<T>) to M.
 *                               Use this to:
 *                               - rename keys (e.g., `status[]` -> `active?: boolean`)
 *                               - coerce types (arrays -> booleans, strings -> numbers)
 *                               - add derived params (sorting, pagination)
 *                               - drop empty/undefined values before hitting the API
 * @param options.stringifyFilters Optional function to serialize the current filters
 *                                into a query string for the browser URL. Signature:
 *                                `(filters: Partial<T>) => string`.
 *                                Defaults to `queryString.stringify(filters)`.
 *                                Supply your own if you need a specific encoding
 *                                (e.g., array format, skipping empties/nulls).
 *
 * @returns {
 *   filters: Partial<T>;                        // current UI filters
 *   apiQueryParams: M;                          // derived API request params
 *   handleFilterChange: (patch: Partial<T>) => void; // merge+update filters
 *   resetFilters: () => void;                   // reset to defaultFilters
 *   resetSingleFilter: (key: keyof T) => void;  // reset a single filter to default
 *   isDirty: boolean;                           // true if filters differ from defaults
 * }
 *
 * @example
 * type T = ExpenseCategoryFilters;
 * type M = ExpenseCategoryFiltersRequestParams;
 *
 * const { filters, apiQueryParams, handleFilterChange, resetFilters, isDirty } =
 *   useFilters<T, M>({
 *     defaultFilters: {
 *       search: '',
 *       status: [],
 *       required: [],
 *     },
 *     apiQueryParams: (f) => ({
 *       // pass through or drop empties as needed
 *       search: f.search || undefined,
 *       // map array-based UI filters to booleans for the API
 *       active: parseBooleanQueryParamFromArray(f.status ?? [], ExpenseCategoryStatus.Active),
 *       required: parseBooleanQueryParamFromArray(f.required ?? [], RequiredStatus.Required),
 *       // add server-side sorting
 *       sortField: ExpenseCategorySortField.Name,
 *       sortDirection: 'ASC',
 *     }),
 *   });
 *
 * // T (UI) and M (API) are intentionally decoupled:
 * // - T models what the component needs to render and collect input.
 * // - M models what the backend needs to execute the query efficiently.
 */
function useFilters<T, M>(
  options: UseFilterOptions<T, M>,
): UseFiltersReturnType<T, M> {
  const history = useHistory<LocationState>()
  const { pathname, search: currentSearch } = useLocation()
  const rawQueryParams = useQueryParams()

  const allowedKeys = useMemo(
    () => Object.keys(options.defaultFilters || {}) as KeyStr<T>[],
    [options.defaultFilters],
  )

  const queryParams = useMemo(() => {
    if (options.normalizeQueryParams) {
      // If provided, use custom normalization function
      return options.normalizeQueryParams(rawQueryParams)
    } else {
      // Default normalization: filter to allowed keys and coerce types
      // The default normalization handles strings and string arrays
      const filtered = Object.fromEntries(
        Object.entries(rawQueryParams).filter(([key]) =>
          allowedKeys.includes(key as KeyStr<T>),
        ),
      )
      // Normalize based on default filter types
      const normalized = Object.fromEntries(
        Object.entries(filtered).map(([key, value]) => {
          const defaultValue = options.defaultFilters?.[key as keyof T]
          // If default is an array, ensure parsed value is also an array
          if (Array.isArray(defaultValue)) {
            return [key, Array.isArray(value) ? value : value ? [value] : []]
          }
          return [key, value]
        }),
      )

      return normalized as Partial<T>
    }
  }, [rawQueryParams, allowedKeys, options.defaultFilters])

  const initialFilters = {
    ...options.defaultFilters,
    ...queryParams,
    ...options.initialFilters,
  }
  const [currentFilters, setCurrentFilters] =
    useState<Partial<T>>(initialFilters)

  const filterToAllowed = useCallback(
    (obj: Partial<T>): Partial<T> =>
      Object.fromEntries(
        Object.entries(obj).filter(([k]) =>
          allowedKeys.includes(k as KeyStr<T>),
        ),
      ) as Partial<T>,
    [allowedKeys],
  )

  const updateCurrentUrl = useCallback(
    (filters: Partial<T>): void => {
      const cleaned = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => {
          if (value === '' || value === undefined || value === null)
            return false
          if (Array.isArray(value) && value.length === 0) return false
          return true
        }),
      ) as Partial<T>

      const stringified = options.stringifyFilters
        ? options.stringifyFilters(cleaned)
        : queryString.stringify(cleaned, {
            skipNull: true,
            skipEmptyString: true,
            arrayFormat: 'none',
          })

      const nextSearch = stringified ? `?${stringified}` : ''
      if (nextSearch !== currentSearch) {
        // avoid no-op replaces
        history.replace({ pathname, search: nextSearch })
      }
    },
    [history, pathname, currentSearch, options.stringifyFilters],
  )

  type FiltersUpdater = Partial<T> | ((prev: Partial<T>) => Partial<T>)

  const updateFilters = useCallback(
    (updater: FiltersUpdater): void => {
      setCurrentFilters((prev) => {
        const nextRaw =
          typeof updater === 'function'
            ? (updater as (p: Partial<T>) => Partial<T>)(prev)
            : updater

        const next = filterToAllowed(nextRaw)
        // Always sync the URL, even if state object is unchanged.
        // updateCurrentUrl itself no-ops if the search string wouldn't change.
        updateCurrentUrl(next)

        // Avoid unnecessary setState re-render if identical.
        return isEqual(prev, next) ? prev : next
      })
    },
    [filterToAllowed, updateCurrentUrl],
  )

  const handleFilterChange = useCallback(
    (patch: Partial<T>) => {
      updateFilters((prev) => {
        const next = { ...prev, ...patch }
        return next
      })
    },
    [updateFilters],
  )

  const resetFilters = useCallback((): void => {
    updateFilters(options.defaultFilters)
  }, [updateFilters, options.defaultFilters])

  const resetSingleFilter = useCallback(
    (key: keyof T): void => {
      updateFilters((prev) => ({ ...prev, [key]: options.defaultFilters[key] }))
    },
    [updateFilters, options.defaultFilters],
  )

  const apiQueryParams = useMemo(
    () => options.apiQueryParams(currentFilters),
    [options.apiQueryParams, currentFilters],
  )

  const debouncedApiParamsJson = useDebounceValue(
    useMemo(() => JSON.stringify(apiQueryParams), [apiQueryParams]),
    300,
  )
  const debouncedApiQueryParams = useMemo(
    () => JSON.parse(debouncedApiParamsJson),
    [debouncedApiParamsJson],
  )

  const { changedKeys, count } = useMemo(() => {
    if (options.diff)
      return options.diff(options.defaultFilters, currentFilters)
    const changed: (keyof T)[] = []
    for (const k of allowedKeys) {
      const eq = options.equalsMap?.[k as KeyStr<T>]
      const a = (options.defaultFilters as any)[k]
      const b = (currentFilters as any)[k]
      const equal = eq ? eq(a, b) : isEqual(a, b)
      if (!equal) changed.push(k as keyof T)
    }
    return { changedKeys: changed, count: changed.length }
  }, [
    options.diff,
    options.defaultFilters,
    currentFilters,
    allowedKeys,
    options.equalsMap,
  ])

  return {
    filters: currentFilters,
    apiQueryParams: debouncedApiQueryParams,
    handleFilterChange: handleFilterChange,
    resetFilters: resetFilters,
    resetSingleFilter: resetSingleFilter,
    isDirty: count > 0,
    selectedFiltersCount: count,
    changedFilterKeys: changedKeys,
  }
}

export default useFilters
